import React, { Suspense, useState, useEffect } from 'react';
import { Container } from '@mui/material';
import { useSelector } from 'react-redux';
import { getSelectedAccount } from '../../../redux/selectedAccountSlice';
import CircularProgress from '@mui/material/CircularProgress';
import {
  useGetSensorsByAccountIdQuery,
  usePatchSensorMutation,
} from '../../../api/recalc/endpoints/sensorsApiSlice';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import { DateTime } from 'luxon';
import { useLazyGetDataQuery } from '../../../api/recalc/endpoints/dataApiSlice';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../redux/snackbarSlice';
import { useNavigate } from 'react-router-dom';

import SensorDataGrid from '@/features/devices/components/sensors/SensorDataGrid';

const toUTC = (timestamp) =>
  DateTime.fromISO(timestamp, { zone: 'utc' }).toMillis();

const formatAndEncodeTimestampForAPI = (date) => {
  const isoString = DateTime.fromJSDate(date, { zone: 'utc' }).toFormat(
    "yyyy-MM-dd'T'HH:mm:ss'Z'",
  );
  return encodeURIComponent(isoString);
};

const SensorsPage = () => {
  const selectedAccount = useSelector(getSelectedAccount);
  const {
    data: sensors,
    error,
    isLoading,
    isSuccess,
  } = useGetSensorsByAccountIdQuery(selectedAccount?.AccountId);
  const [patchSensor] = usePatchSensorMutation();
  const [fetchData, { data: data, isLoading: isLoadingData }] =
    useLazyGetDataQuery();
  const [unitData, setUnitData] = React.useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [renderChart, setRenderChart] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSupportClick = () => {
    navigate('/support');
  };

  const fetchDataForUnit = async (unit) => {
    setLoadingData(true);
    if (selectedSensor) {
      const from = DateTime.now().minus({ hours: 1 }).toMillis();
      const to = DateTime.now().toMillis();

      try {
        const seriesData = await Promise.all(
          unit.UnitSensors?.map(async (unitSensor) => {
            const fetchedData = await fetchData({
              from: formatAndEncodeTimestampForAPI(new Date(from)),
              to: formatAndEncodeTimestampForAPI(new Date(to)),
              unitId: unitSensor.Unit.UnitId,
              datatype: 'uncalibrated-accumulated-flow',
              unitTypeInputId: unitSensor.UnitTypeInputId,
              aggregateType: 'Raw',
            }).unwrap();

            return {
              name: `(${unitSensor?.Unit.UnitId}) ${unitSensor?.Unit?.Name}`,
              base: fetchedData[0]?.Base,
              data: fetchedData?.map((item) => [
                toUTC(item.Timestamp) + 3600000,
                parseFloat(item.Value.toFixed(10)),
                item.Base,
              ]),
              type: 'spline',
              showInNavigator: true,
            };
          }),
        );
        setUnitData(
          seriesData?.filter(
            (series) => series !== null && series !== undefined,
          ),
        );
        setLoadingData(false);
      } catch (error) {
        console.log('Error fetching data:', error);
      }
    } else {
      setUnitData(null);
    }
  };

  useEffect(() => {
    const shouldFetch = selectedSensor !== null;

    if (shouldFetch) {
      fetchDataForUnit(selectedSensor?.sensorObject);
      setRenderChart(true);

      const intervalId = setInterval(() => {
        fetchDataForUnit(selectedSensor?.sensorObject);
      }, 10000); // Poll every 10 seconds

      return () => clearInterval(intervalId); // Cleanup interval on component unmount
    } else {
      setUnitData(null);
      setRenderChart(false); // Disable the chart rendering if no units are associated
    }
  }, [selectedSensor]);

  const handlePatchSensor = async (patch) => {
    try {
      await patchSensor({ id: selectedSensor.SensorId, patch: patch });
      dispatch(
        showSnackbar({
          message: `Sensor patched successfully with calibration factor ${patch.VolumeConstant}`,
          severity: 'success',
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `Error patching sensor: ${error}`,
          severity: 'error',
        }),
      );
    }
  };

  return (
    <Container maxWidth="xxl">
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '80vh',
              verticalAlign: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        }
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '80vh',
              verticalAlign: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Fade in={isSuccess} timeout={500}>
            <Box>
              <SensorDataGrid
                accountId={selectedAccount?.AccountId}
                sensors={sensors}
                isLoading={isLoading || loadingData}
                setSelectedSensor={setSelectedSensor}
                unitData={unitData}
                isLoadingData={isLoadingData}
                renderChart={renderChart}
                handlePatchSensor={handlePatchSensor}
                handleSupportClick={handleSupportClick}
              />
            </Box>
          </Fade>
        )}
      </Suspense>
    </Container>
  );
};

export default SensorsPage;
